<template>
    <div style="height: 100%">
        <div id="main" v-show="!rotaAninhada">
            <container-loader v-if="isLoading && !$store.getters.isLoading"/>
            <div class="page">
                <div class="vertical-flow">
                    <div class="listing-header">
                        <!-- TÍTULO E BREADCRUMB -->
                        <div class="title-breadcrumb">
                            <h1 class="title">{{ $t("centro-custo") }}</h1>
                            <nav class="breadcrumb" aria-label="breadcrumbs">
                                <ul>
                                    <li>
                                        <a class="a-inativo">{{
                                            $t($route.meta.contexto)
                                        }}</a>
                                    </li>
                                    <li>
                                        <a class="a-inativo">{{
                                            $t("financeiro")
                                        }}</a>
                                    </li>
                                    <li>
                                        <a aria-current="page">{{
                                            $t("centro-custo")
                                        }}</a>
                                    </li>
                                </ul>
                            </nav>
                        </div>

                        <!-- BOTÕES -->
                        <div class="header-buttons">
                            <button
                                v-if="permissao.create"
                                class="save-btn"
                                type="button"
                                tag="button"
                                @click="$router.push({ name: `${$route.meta.abrev}-centro-custo-new` })"
                            >
                                {{ $t("adicionar") }}
                            </button>
                        </div>
                    </div>

                    <!-- PESQUISA E FILTROS -->
                    <div class="columns is-multiline">
                        <div class="column is-8">
                            <search-bar
                                :onSearch="debouncedOnSearch"
                            />
                        </div>
                        <div class="column is-4">
                            <vss-select
                                ref="situacaoSelect"
                                :searchable="false"
                                :options="situacaoSelect"
                                v-model="situacaoSelected"
                                mlLabels
                                title="situacao"
                                clearable
                                @input="onSituacaoChanged"
                                :height="40"
                            />
                        </div>
                    </div>

                    <!-- LISTAGEM -->
                    <div class="registry-wrapper">
                        <empty-table-message v-if="isEmpty" />
                        <b-table
                            v-else
                            :data="centroCusto"
                            class="data-table"
                            @sort="sortTable"
                            backend-sorting
                        >
                            <b-table-column
                                v-for="(f, i) in fields"
                                :key="i"
                                :field="f.key"
                                :label="f.key != 'excluir' ? $t(f.key) : ''"
                                :width="f.width"
                                :sortable="f.sortable"
                                :custom-sort="f.sortFn"
                                v-slot="props"
                                :class="{
                                    'excluir-field': f.key == 'excluir',
                                }"
                            >
                                <div v-if="f.key == 'descricao'">
                                    <router-link
                                        :to="{
                                            name: `${$route.meta.abrev}-centro-custo-edit`,
                                            params: {
                                                id: props.row.idfin_centro_custo,
                                            },
                                        }"
                                    >
                                        <pre>{{ props.row[f.key] }}</pre>
                                    </router-link>
                                </div>
                                <div v-else-if="f.key == 'excluir' && !tabelaIntegracao && permissao.update" >
                                    <b-button
                                        class="action-button"
                                        :type="
                                            props.row['situacao'] == 1
                                                ? 'is-danger'
                                                : 'is-success'
                                        "
                                        :icon-right="
                                            props.row['situacao'] == 1
                                                ? 'delete'
                                                : 'check'
                                        "
                                        @click="
                                            promptChangeSituacao(
                                                props.row
                                            )
                                        "
                                        v-tippy 
                                        :title="$t(
                                            props.row['situacao'] == 1
                                                ? 'desativar'
                                                : 'reativar'
                                        )"
                                    />
                                </div>
                                <div v-else-if="f.key == 'situacao'">
                                    <b-tag
                                        v-if="props.row[f.key] == 1"
                                        class="table-tag table-tag-full-width"
                                        type="is-success"
                                        size="is-medium"
                                    >
                                        {{ $t("ativo") }}
                                    </b-tag>
                                    <b-tag
                                        v-else
                                        class="table-tag table-tag-full-width"
                                        type="is-danger"
                                        size="is-medium"
                                    >
                                        {{ $t("inativo") }}
                                    </b-tag>
                                </div>
                                <div v-else>
                                    {{ props.row[f.key] }}
                                </div>
                            </b-table-column>
                        </b-table>
                    </div>
                    <PaginationViewer
                        class="mb-0"
                        :resetPagination="pagination.resetPagination"
                        :totalItems="pagination.totalRows"
                        :perPageOptions="pagination.perPageOptions"
                        :optionStarted="pagination.optionStarted"
                        @change="updatePagination"
                    />
                </div>
            </div>
        </div>
        <router-view />
    </div>
</template>

<script>
import CentroCustoService from "@/services/vendas/financeiro/CentroCustoService.js";
import ContainerLoader from "@/components/content/loader/ContainerLoader";

export default {
    beforeRouteUpdate(to, from, next) {
        if (
            to.name.includes(`${to.meta.abrev}-centro-custo`) 
            && to.name !=`${to.meta.abrev}-centro-custo-list`
        ) {
            this.rotaAninhada = true;
            next();
        } else {
            this.rotaAninhada = false;
            this.carregarDados(this.pagination.currentPage);
            next();
        }
    },
    components: {
        ContainerLoader,
    },
    data() {
        return {
            rotaAninhada: false,
            centroCusto: [],
            service: new CentroCustoService(this.$http, this.$router),
            tabelaIntegracao: this.$store.state.user.tabelasERPWebServer.includes("FIN_CENTRO_CUSTO"),
            fields: [
                { key: "codigo", width: '7.5%', sortable: true, sortFn: null,},
                { key: "descricao", width: null, sortable: true, sortFn: null },
                { key: "situacao", width: 70, sortable: false, sortFn: null },
                { key: "excluir", width: 20, sortable: false, sortFn: null },
            ],
            isLoading: false,
            isEmpty: false,
            queryFilter: "",
            debouncedOnSearch: null,
            sort: {
                order: "desc",
                column: "descricao",
            },
            situacaoSelected: { id: 4, label: "todos" },
            situacaoSelect: [
                { id: 2, label: "todos" },
                { id: 0, label: "inativos" },
                { id: 1, label: "ativos" },
            ],
            permissao: {},
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
        };
    },
    created() {
        this.rotaAninhada = (
            this.$route.name.includes(`${this.$route.meta.abrev}-centro-custo`) 
            && this.$route.name != `${this.$route.meta.abrev}-centro-custo-list`
        );

        this.pagination.perPage =
            this.pagination.perPageOptions[this.pagination.optionStarted];

        if (this.$route.meta.contexto == "vendas") {
            this.permissao = this.hasPermissionAccess(
                "vendas",
                "financeiro",
                "ven-centro-custo"
            );
        } else if (this.$route.meta.contexto == "despesas") {
            this.permissao = this.hasPermissionAccess(
                "despesas",
                "cadastros",
                "des-centro-custo"
            );
        }

        !this.permissao.read &&
            this.$router.push({
                name: "acesso-nao-permitido",
                params: { tela: "centro-custo" },
            });

        this.debouncedOnSearch = this.debounce(this.onSearch, 500);
        this.carregarDados();
    },
    methods: {
        sortTable(field, order) {
            this.sort.column = field;
            this.sort.order = order;
            this.carregarDados();
        },
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        onSearch(value) {
            this.queryFilter = this.removeToSearch(value);
            this.carregarDados(1);
        },
        carregarDados(currentPage = 1) {
            if (currentPage == 1 && this.pagination.currentPage != 1)
                this.pagination.resetPagination = true;

            this.isLoading = true;

            this.service
                .findAll(
                    this.situacaoSelected.id,
                    this.sort.column,
                    this.sort.order,
                    this.pagination.perPage,
                    currentPage - 1,
                    this.queryFilter
                )
                .then(this.mapItems)
                .then((value) => {
                    this.centroCusto = value;
                    this.isEmpty = this.centroCusto.length <= 0;
                })
                .catch((e) => {
                    this.isEmpty = true;
                    this.$root.showMessage("error", "msg-erro-load");
                })
                .finally((_) => {
                    this.isLoading = false;
                    this.pagination.resetPagination = false;
                });
        },
        mapItems(body) {
            if (body && body.content) {
                this.pagination.totalRows = body.totalElements;
                return body.content.map((m) => {
                    return {
                        idfin_centro_custo: m.idfin_centro_custo,
                        codigo: m.idfin_centro_custoerp,
                        descricao: m.descricao,
                        situacao: m.situacao,
                    };
                });
            } else {
                return [];
            }
        },
        onSituacaoChanged(val) {
            this.situacaoSelected = val 
                ? this.situacaoSelect.find((v) => v.id == val.id)
                : this.situacaoSelect[0];
            this.carregarDados();
        },
        promptChangeSituacao(row) {
            let message = this.$t(
                    row.situacao == 1
                        ? "msg-confirmacao-desativacao-n"
                        : "msg-confirmacao-reativacao-n", 
                    { n: row.descricao }
                );
            this.$root.confirm({
                message: message,
                onConfirm: _ => this.updateSituacaoRegistro(row),
            });
        },
        updateSituacaoRegistro(row) {
            let finCentroCusto = {
                idfin_centro_custo: row.idfin_centro_custo,
                situacao: row.situacao == 1 ? 0 : 1,
            };

            let tempFailure = this.$t(
                    finCentroCusto.situacao == 1
                        ? "msg-erro-ao-desativar"
                        : "msg-erro-ao-reativar", 
                    { n: this.$t("centro-custo") }
                );

            this.isLoading = true;

            this.service
                .updateSituacao(finCentroCusto)
                .then((r) => {
                    if (r.status == 200) {
                        let tempSuccess = this.$t(
                                finCentroCusto.situacao == 0
                                    ? "msg-sucesso-ao-desativar"
                                    : "msg-sucesso-ao-reativar", 
                                { n: this.$t("centro-custo") }
                            );
                        this.$root.showMessage(
                            "success",
                            tempSuccess,
                            true,
                            false
                        );
                    } else if (r.status == 500) {
                        this.$root.showMessage(
                            "error",
                            "Erro ao atualizar status."
                        );
                    }
                })
                .catch((e) => {
                    this.$root.showMessage("error", tempFailure, true, false);
                })
                .finally((_) => {
                    this.carregarDados(this.pagination.currentPage);
                });
        },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: 100%;
    // .page {
    //     position: relative;
    //     padding-left: 0;
    // }
}

</style>